import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ShieldCheck, Activity, MonitorSmartphone, AlertTriangle, LockKeyhole,
  LogOut, ScrollText, Gauge,
} from "lucide-react";

const CAT_LABEL = { auth: "Autenticación", permission: "Permisos", data: "Datos", export: "Exportación" };
const SEV_STYLE = {
  info: "text-muted-foreground border-border",
  warning: "text-amber-600 border-amber-500/50 bg-amber-500/10",
  critical: "text-destructive border-destructive/50 bg-destructive/10",
};
const TABS = [
  { key: "overview", label: "Panel", icon: Gauge },
  { key: "audit", label: "Bitácora", icon: ScrollText },
  { key: "sessions", label: "Sesiones", icon: MonitorSmartphone },
  { key: "alerts", label: "Alertas", icon: AlertTriangle },
];

const fmtDate = (s) => (s ? new Date(s).toLocaleString("es-MX") : "—");

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <div className="border border-border rounded-sm p-4" data-testid={`metric-${label}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className={`mt-2 font-mono-num text-3xl font-black ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function SecurityView() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [audit, setAudit] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const loadOverview = async () => { try { const { data } = await api.get("/security/overview"); setOv(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const loadAudit = async () => { const { data } = await api.get("/security/audit?limit=200"); setAudit(data); };
  const loadSessions = async () => { const { data } = await api.get("/security/sessions"); setSessions(data); };
  const loadAlerts = async () => { const { data } = await api.get("/security/alerts"); setAlerts(data); };

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    if (tab === "audit") loadAudit();
    if (tab === "sessions") loadSessions();
    if (tab === "alerts") loadAlerts();
    if (tab === "overview") loadOverview();
  }, [tab]);

  const revoke = async (jti) => {
    try { await api.post(`/security/sessions/${jti}/revoke`); toast.success("Sesión revocada"); loadSessions(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const scoreTone = (s) => (s >= 80 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-destructive");

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="security-view">
      <div className="h-12 shrink-0 border-b border-border flex items-center px-3 gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h2 className="font-head font-bold">Centro de Seguridad</h2>
        <div className="flex-1" />
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sec-tab-${t.key}`}
              className={`text-xs px-3 h-8 rounded-sm border transition-colors flex items-center gap-1 ${tab === t.key ? "bg-accent border-primary text-foreground" : "border-border text-muted-foreground hover:bg-accent/40"}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-5">
        {tab === "overview" && ov && (
          <div className="space-y-5 max-w-4xl">
            <div className="border border-border rounded-sm p-5 flex items-center gap-5" data-testid="security-score">
              <div className={`font-mono-num text-6xl font-black ${scoreTone(ov.score)}`}>{ov.score}</div>
              <div>
                <div className="text-sm font-medium">Puntaje de seguridad</div>
                <div className="text-xs text-muted-foreground">Bloqueo tras {ov.config.max_failed} intentos · {ov.config.lockout_min} min · ventana {ov.config.window_min} min</div>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Metric icon={Activity} label="Fallos 24h" value={ov.failed_24h} tone={ov.failed_24h ? "text-amber-600" : ""} />
              <Metric icon={LockKeyhole} label="Bloqueadas" value={ov.locked_accounts} tone={ov.locked_accounts ? "text-destructive" : ""} />
              <Metric icon={MonitorSmartphone} label="Sesiones" value={ov.active_sessions} />
              <Metric icon={AlertTriangle} label="Alertas 24h" value={ov.alerts_24h} tone={ov.alerts_24h ? "text-amber-600" : ""} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Actividad reciente</div>
              <div className="space-y-1.5">
                {ov.recent.map((e) => (
                  <div key={e.id} className={`text-xs border rounded-sm px-3 py-2 flex items-center justify-between ${SEV_STYLE[e.severity] || SEV_STYLE.info}`}>
                    <span><b>{e.action}</b> · {e.detail} <span className="text-muted-foreground">({e.email || "—"})</span></span>
                    <span className="font-mono-num text-muted-foreground">{fmtDate(e.created_at)}</span>
                  </div>
                ))}
                {ov.recent.length === 0 && <div className="text-xs text-muted-foreground">Sin eventos.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "audit" && (
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="qc-table">
              <thead><tr><th style={{ width: 160 }}>Fecha</th><th style={{ width: 110 }}>Severidad</th><th style={{ width: 130 }}>Categoría</th><th>Acción</th><th>Detalle</th><th style={{ width: 160 }}>Usuario</th><th style={{ width: 120 }}>IP</th></tr></thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} data-testid={`audit-row-${e.id}`}>
                    <td className="font-mono-num text-xs">{fmtDate(e.created_at)}</td>
                    <td><span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm border ${SEV_STYLE[e.severity]}`}>{e.severity}</span></td>
                    <td className="text-xs">{CAT_LABEL[e.category] || e.category}</td>
                    <td className="text-xs font-medium">{e.action}</td>
                    <td className="text-xs text-muted-foreground">{e.detail}</td>
                    <td className="text-xs font-mono-num">{e.email || "—"}</td>
                    <td className="text-xs font-mono-num text-muted-foreground">{e.ip || "—"}</td>
                  </tr>
                ))}
                {audit.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Sin registros.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "sessions" && (
          <div className="border border-border rounded-sm overflow-hidden max-w-4xl">
            <table className="qc-table">
              <thead><tr><th>Usuario</th><th style={{ width: 130 }}>IP</th><th>Dispositivo</th><th style={{ width: 160 }}>Última actividad</th><th style={{ width: 90 }}></th></tr></thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.jti} data-testid={`session-row-${s.jti}`}>
                    <td className="text-xs font-mono-num">{s.email}</td>
                    <td className="text-xs font-mono-num text-muted-foreground">{s.ip || "—"}</td>
                    <td className="text-xs text-muted-foreground truncate max-w-xs">{s.user_agent || "—"}</td>
                    <td className="text-xs font-mono-num">{fmtDate(s.last_seen)}</td>
                    <td className="text-right"><Button size="sm" variant="outline" className="h-7 rounded-sm gap-1 text-destructive" onClick={() => revoke(s.jti)} data-testid={`revoke-session-${s.jti}`}><LogOut className="h-3.5 w-3.5" /> Cerrar</Button></td>
                  </tr>
                ))}
                {sessions.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-6 text-xs">Sin sesiones activas.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "alerts" && (
          <div className="space-y-1.5 max-w-4xl">
            {alerts.map((e) => (
              <div key={e.id} className={`text-sm border rounded-sm px-3 py-2 flex items-center justify-between ${SEV_STYLE[e.severity]}`} data-testid={`alert-${e.id}`}>
                <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /><b>{e.action}</b> · {e.detail} <span className="text-muted-foreground text-xs">({e.email || "—"} · {e.ip || "—"})</span></span>
                <span className="font-mono-num text-xs text-muted-foreground">{fmtDate(e.created_at)}</span>
              </div>
            ))}
            {alerts.length === 0 && <div className="text-xs text-muted-foreground">Sin alertas de seguridad. 🎉</div>}
          </div>
        )}
      </div>
    </div>
  );
}
